import { useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  InputAdornment,
  OutlinedInput,
  FormControl,
  Link,
  Button,
  Modal,
} from "@mui/material";
import SettingTitle from "../component/SettingTitle";
import { styles } from "./stylesChangePassword";
import EyeSvg from "@/assets/svg/EyeSvg";
import { useChangePasswordMutation, useRequestChangePasswordOtpMutation } from "@/store/api/authApi";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import OtpFormModal from "@/components/UI/modal/OtpFormModal/OtpFormModal";
import { books } from "@/assets/books";
import theme from "@/theme";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/hooks/useAppSelector";

const passwordRegex = /^(?=.*\d)(?=.*[^\w\s]).{8,}$/;

const ChangePassword = () => {
  const navigate = useNavigate();
  const authUser = useAppSelector((state) => state.auth.user);
  const [requestChangePasswordOtp] = useRequestChangePasswordOtpMutation();
  const [changePassword] = useChangePasswordMutation();
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);
  const [values, setValues] = useState({ current: "", next: "", confirm: "" });
  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [otpModalOpen, setOtpModalOpen] = useState(false);

  const handleSave = async () => {
    setCurrentPasswordError("");
    try {
      await requestChangePasswordOtp({
        currentPassword: values.current,
        newPassword: values.next,
        passwordconf: values.confirm,
      }).unwrap();
      setOtpModalOpen(true);
    } catch (err: any) {
      const message = err?.data?.message;
      if (message === "Current password is incorrect") {
        setCurrentPasswordError(message);
      } else {
        setToast({
          message: Array.isArray(message) ? message[0] : message || "Something went wrong",
          success: false,
        });
      }
    }
  };

  const handleOtpSubmit = async (otpCode: string) => {
    await changePassword({
      currentPassword: values.current,
      newPassword: values.next,
      passwordconf: values.confirm,
      otp: otpCode,
    }).unwrap();
    setOtpModalOpen(false);
    setValues({ current: "", next: "", confirm: "" });
    navigate("/settings/account", {
      state: { message: "The password has been successfully changed.", success: true },
    });
  };

  const handleResendOtp = async () => {
    await requestChangePasswordOtp({
      currentPassword: values.current,
      newPassword: values.next,
      passwordconf: values.confirm,
    }).unwrap();
  };

  const handleCloseOtpModal = () => setOtpModalOpen(false);

  const nextPasswordError = useMemo(() => {
    if (!values.next) return "";
    if (!passwordRegex.test(values.next))
      return "Password must contain 8 characters, a number and a symbol";
    return "";
  }, [values.next]);

  const confirmPasswordError = useMemo(() => {
    if (!values.confirm) return "";
    if (values.next && values.confirm && values.next !== values.confirm)
      return "Passwords do not match";
    return "";
  }, [values.next, values.confirm]);

  const isValid = useMemo(() => {
    const basicOk =
      values.current.length > 0 &&
      passwordRegex.test(values.next) &&
      values.next === values.confirm &&
      !nextPasswordError &&
      !confirmPasswordError;
    return basicOk;
  }, [values]);

  return (
    <>
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}
      <Modal
        open={otpModalOpen}
        onClose={handleCloseOtpModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      >
        <OtpFormModal
          email={authUser?.email || "your email"}
          onSubmit={handleOtpSubmit}
          onResend={handleResendOtp}
          onHeaderClick={handleCloseOtpModal}
        />
      </Modal>
      <SettingTitle title="Change password" back submit={handleSave} isActive={true} />
      <Box sx={styles.formRoot} component="form" autoComplete="off">
        <Box sx={styles.inputsBlock}>
          <input type="password" autoComplete="new-password" style={{ display: "none" }} />
          <Box sx={styles.inputGroup}>
            <Typography sx={styles.label}>Current Password</Typography>
            <FormControl fullWidth>
              <OutlinedInput
                value={values.current}
                onChange={(e) => {
                  setCurrentPasswordError("");
                  setValues((s) => ({ ...s, current: e.target.value }));
                }}
                placeholder="Enter your current password"
                type={show.current ? "text" : "password"}
                error={!!currentPasswordError}
                sx={{
                  ...styles.input,
                  "& input": {
                    WebkitTextSecurity: !show.current ? "*" : "none",
                  },
                  ...(currentPasswordError && {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E62A3B",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#E62A3B" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E62A3B",
                      borderWidth: "1px",
                    },
                  }),
                }}
                autoComplete="off"
                name="currentPwd"
                id="currentPwd"
                endAdornment={
                  <InputAdornment position="end" sx={styles.endAdornment}>
                    <IconButton
                      size="small"
                      onClick={() => setShow((s) => ({ ...s, current: !s.current }))}
                      edge="end"
                      aria-label="toggle current password visibility"
                      sx={styles.iconButton}
                    >
                      <EyeSvg
                        crossed={!show.current}
                        color={values.current ? "#383C40" : "#C6CACE"}
                      />
                    </IconButton>
                  </InputAdornment>
                }
              />
              {currentPasswordError && (
                <Typography sx={{ color: "red", fontSize: "0.85rem", mt: 0.5 }}>
                  {currentPasswordError}
                </Typography>
              )}
            </FormControl>
            <Link href={books.forgotPassword} sx={styles.Link}>
              Forgot password?
            </Link>
          </Box>

          <Box sx={styles.inputGroup}>
            <Typography sx={styles.label}>New Password</Typography>
            <FormControl fullWidth>
              <OutlinedInput
                value={values.next}
                onChange={(e) => setValues((s) => ({ ...s, next: e.target.value }))}
                placeholder="Enter the new password"
                type={show.next ? "text" : "password"}
                error={!!nextPasswordError}
                sx={{
                  ...styles.input,
                  "& input": {
                    WebkitTextSecurity: !show.next ? "*" : "none",
                  },
                  ...(nextPasswordError && {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E62A3B",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#E62A3B" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E62A3B",
                      borderWidth: "1px",
                    },
                  }),
                }}
                autoComplete="off"
                name="newPwd"
                id="newPwd"
                endAdornment={
                  <InputAdornment position="end" sx={styles.endAdornment}>
                    <IconButton
                      size="small"
                      onClick={() => setShow((s) => ({ ...s, next: !s.next }))}
                      edge="end"
                      aria-label="toggle new password visibility"
                      sx={styles.iconButton}
                    >
                      <EyeSvg crossed={!show.next} color={values.next ? "#383C40" : "#C6CACE"} />
                    </IconButton>
                  </InputAdornment>
                }
              />
              {nextPasswordError && (
                <Typography sx={{ color: "red", fontSize: "0.85rem", mt: 0.5 }}>
                  {nextPasswordError}
                </Typography>
              )}
            </FormControl>
          </Box>

          <Box sx={{ ...styles.inputGroup, mt: "-14px" }}>
            <Typography sx={styles.label}>Confirm Password</Typography>
            <FormControl fullWidth>
              <OutlinedInput
                value={values.confirm}
                onChange={(e) => setValues((s) => ({ ...s, confirm: e.target.value }))}
                placeholder="Confirm the password"
                type={show.confirm ? "text" : "password"}
                error={!!confirmPasswordError}
                sx={{
                  ...styles.input,
                  "& input": {
                    WebkitTextSecurity: !show.confirm ? "*" : "none",
                  },
                  ...(confirmPasswordError && {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E62A3B",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#E62A3B" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E62A3B",
                      borderWidth: "1px",
                    },
                  }),
                }}
                autoComplete="off"
                name="confirmPwd"
                id="confirmPwd"
                endAdornment={
                  <InputAdornment position="end" sx={styles.endAdornment}>
                    <IconButton
                      size="small"
                      onClick={() => setShow((s) => ({ ...s, confirm: !s.confirm }))}
                      edge="end"
                      aria-label="toggle confirm password visibility"
                      sx={styles.iconButton}
                    >
                      <EyeSvg
                        crossed={!show.confirm}
                        color={values.confirm ? "#383C40" : "#C6CACE"}
                      />
                    </IconButton>
                  </InputAdornment>
                }
              />
              {confirmPasswordError && (
                <Typography sx={{ color: "red", fontSize: "0.85rem", mt: 0.5 }}>
                  {confirmPasswordError}
                </Typography>
              )}
            </FormControl>
          </Box>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              width: "100%",
              mt: "4px",
              bgcolor: isValid ? theme.palette.custom.mainBlue : "#8BC5F4",
              borderRadius: "28px",
              height: "50px",
              color: "#fff",
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 500,
              lineHeight: "22px",
              padding: "14px 24px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: isValid ? theme.palette.custom.mainBlue : "#8BC5F4",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: "#8BC5F4",
                color: "#FFFFFF",
                opacity: 1,
              },
            }}
            disabled={!isValid}
          >
            Save
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default ChangePassword;
